import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useBible } from '../contexts/BibleContext';
import { useNavigate } from 'react-router-dom';
import { searchVerses } from '../services/localBibleApi';
import Icon from '../components/UI/Icon';
import './SearchPage.css';

// Text similarity calculation (moved outside component for stability)
function calculateSimilarity(text1, text2) {
  const tokenize = (text) => {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length > 2);
  };
  
  const tokens1 = new Set(tokenize(text1));
  const tokens2 = new Set(tokenize(text2));
  
  const intersection = new Set([...tokens1].filter(x => tokens2.has(x)));
  const union = new Set([...tokens1, ...tokens2]);
  
  if (union.size === 0) return 0;
  return intersection.size / union.size;
}

// Cluster verses by similarity (moved outside component for stability)
function clusterVerses(verses, threshold = 0.15) {
  const clusters = [];
  const assigned = new Set();
  
  verses.forEach((verse, i) => {
    if (assigned.has(i)) return;
    
    const cluster = [verse];
    assigned.add(i);
    
    verses.forEach((otherVerse, j) => {
      if (i !== j && !assigned.has(j)) {
        const sim = calculateSimilarity(verse.text, otherVerse.text);
        if (sim >= threshold) {
          cluster.push(otherVerse);
          assigned.add(j);
        }
      }
    });
    
    if (cluster.length > 0) {
      clusters.push(cluster);
    }
  });
  
  return clusters;
}

// Generate color for cluster (moved outside component for stability)
function getClusterColor(index) {
  const colors = [
    '#8B7355', '#6B8E4E', '#5D7A99', '#9B6B6B', '#8B6B99',
    '#C4A35A', '#7A9B7A', '#7A7A9B', '#9B8B7A', '#6B9B9B'
  ];
  return colors[index % colors.length];
}

function SearchPage() {
  const navigate = useNavigate();
  const { selectedTranslations, availableBibles } = useBible();
  const [activeTab, setActiveTab] = useState('organic');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [selectedTranslation, setSelectedTranslation] = useState(selectedTranslations?.[0] || 'eng_niv');
  const [error, setError] = useState(null);

  // Graph state for thematic search
  const [thematicQuery, setThematicQuery] = useState('');
  const [isThematicSearching, setIsThematicSearching] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);
  const containerRef = useRef(null);

  // Load search history
  useEffect(() => {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  // Build graph data
  const buildGraph = useCallback((verses) => {
    if (!verses || verses.length === 0) {
      setGraphData({ nodes: [], links: [] });
      return;
    }

    const clusters = clusterVerses(verses);
    const nodes = [];
    const links = [];
    
    clusters.forEach((cluster, clusterIndex) => {
      const color = getClusterColor(clusterIndex);
      const clusterId = `cluster_${clusterIndex}`;
      
      nodes.push({
        id: clusterId,
        type: 'cluster',
        label: `Cluster ${clusterIndex + 1}`,
        color: color,
        size: 18 + cluster.length * 2,
        verses: cluster
      });
      
      cluster.forEach((verse, verseIndex) => {
        const verseId = `verse_${clusterIndex}_${verseIndex}`;
        nodes.push({
          id: verseId,
          type: 'verse',
          label: verse.reference,
          text: verse.text,
          reference: verse.reference,
          osisId: verse.id,
          bookId: verse.bookId,
          chapter: verse.chapter,
          color: color,
          size: 10,
          clusterId: clusterId
        });
        
        links.push({
          source: clusterId,
          target: verseId,
          strength: 0.8
        });
      });
      
      cluster.forEach((verse1, i) => {
        cluster.forEach((verse2, j) => {
          if (i < j) {
            const sim = calculateSimilarity(verse1.text, verse2.text);
            if (sim > 0.2) {
              links.push({
                source: `verse_${clusterIndex}_${i}`,
                target: `verse_${clusterIndex}_${j}`,
                strength: sim
              });
            }
          }
        });
      });
    });
    
    clusters.forEach((cluster1, i) => {
      clusters.forEach((cluster2, j) => {
        if (i < j) {
          let maxSim = 0;
          cluster1.slice(0, 3).forEach(v1 => {
            cluster2.slice(0, 3).forEach(v2 => {
              const sim = calculateSimilarity(v1.text, v2.text);
              if (sim > maxSim) maxSim = sim;
            });
          });
          
          if (maxSim > 0.1) {
            links.push({
              source: `cluster_${i}`,
              target: `cluster_${j}`,
              strength: maxSim * 0.5
            });
          }
        }
      });
    });
    
    setGraphData({ nodes, links });
  }, []);

  // Handle thematic search
  const handleThematicSearch = useCallback(async (searchTerm) => {
    const term = searchTerm || thematicQuery;
    if (!term.trim()) return;
    setThematicQuery(term);
    setIsThematicSearching(true);
    setSelectedVerse(null);

    try {
      const translation = selectedTranslations?.[0] || 'eng_niv';
      const results = await searchVerses(translation, term, { limit: 60 });
      
      if (results && results.length > 0) {
        buildGraph(results);
      } else {
        setGraphData({ nodes: [], links: [] });
      }
    } catch (err) {
      console.error('Thematic search failed:', err);
    } finally {
      setIsThematicSearching(false);
    }
  }, [thematicQuery, selectedTranslations, buildGraph]);

  // D3-like Force Simulation for graph
  useEffect(() => {
    if (!svgRef.current || graphData.nodes.length === 0) return;

    const svg = svgRef.current;
    const container = containerRef.current;
    const width = container?.clientWidth || 800;
    const height = container?.clientHeight || 500;

    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    svg.appendChild(g);

    const linkGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    linkGroup.setAttribute('class', 'links');
    g.appendChild(linkGroup);

    const nodeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    nodeGroup.setAttribute('class', 'nodes');
    g.appendChild(nodeGroup);

    const nodes = graphData.nodes.map((node) => ({
      ...node,
      x: width / 2 + (Math.random() - 0.5) * 300,
      y: height / 2 + (Math.random() - 0.5) * 300,
      vx: 0,
      vy: 0
    }));

    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    
    const links = graphData.links.map(link => ({
      source: nodeMap.get(link.source),
      target: nodeMap.get(link.target),
      strength: link.strength
    })).filter(l => l.source && l.target);

    links.forEach(link => {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('class', 'link');
      line.setAttribute('stroke', '#999');
      line.setAttribute('stroke-opacity', Math.min(link.strength * 2, 0.5));
      line.setAttribute('stroke-width', Math.max(1, link.strength * 2));
      link.element = line;
      linkGroup.appendChild(line);
    });

    nodes.forEach(node => {
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.setAttribute('class', `node ${node.type}`);
      group.style.cursor = 'pointer';
      
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('r', node.size);
      circle.setAttribute('fill', node.color);
      circle.setAttribute('stroke', '#fff');
      circle.setAttribute('stroke-width', node.type === 'cluster' ? 2 : 1.5);
      
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.textContent = node.type === 'cluster' ? '' : node.label;
      text.setAttribute('dy', node.size + 12);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '9px');
      text.setAttribute('fill', 'var(--text-color)');
      text.setAttribute('pointer-events', 'none');
      
      group.appendChild(circle);
      group.appendChild(text);
      
      group.addEventListener('click', () => {
        if (node.type === 'verse') {
          setSelectedVerse(node);
        } else if (node.type === 'cluster') {
          if (node.verses && node.verses.length > 0) {
            setSelectedVerse({
              type: 'cluster',
              label: `${node.verses.length} related verses`,
              verses: node.verses,
              color: node.color
            });
          }
        }
      });
      
      group.addEventListener('mouseenter', () => {
        circle.setAttribute('stroke', '#333');
        circle.setAttribute('stroke-width', node.type === 'cluster' ? 3 : 2);
        setHoveredNode(node);
      });
      
      group.addEventListener('mouseleave', () => {
        circle.setAttribute('stroke', '#fff');
        circle.setAttribute('stroke-width', node.type === 'cluster' ? 2 : 1.5);
        setHoveredNode(null);
      });
      
      let isDragging = false;
      let dragStartX, dragStartY;
      
      const onMouseDown = (e) => {
        isDragging = true;
        dragStartX = e.clientX - node.x;
        dragStartY = e.clientY - node.y;
        node.fx = node.x;
        node.fy = node.y;
        e.stopPropagation();
      };
      
      const onMouseMove = (e) => {
        if (!isDragging) return;
        node.fx = e.clientX - dragStartX;
        node.fy = e.clientY - dragStartY;
        node.x = node.fx;
        node.y = node.fy;
      };
      
      const onMouseUp = () => {
        if (isDragging) {
          isDragging = false;
          node.fx = null;
          node.fy = null;
        }
      };
      
      group.addEventListener('mousedown', onMouseDown);
      group.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      
      node.element = group;
      node.cleanup = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      nodeGroup.appendChild(group);
    });

    const alpha = { value: 1 };
    const alphaDecay = 0.02;
    const alphaMin = 0.001;
    
    const tick = () => {
      if (alpha.value < alphaMin) return;
      
      nodes.forEach(node => {
        if (node.fx == null) {
          node.vx += (width / 2 - node.x) * 0.01 * alpha.value;
          node.vy += (height / 2 - node.y) * 0.01 * alpha.value;
        }
      });
      
      links.forEach(link => {
        const dx = link.target.x - link.source.x;
        const dy = link.target.y - link.source.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const targetDist = 80;
        const force = (dist - targetDist) * 0.05 * link.strength * alpha.value;
        
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        
        if (link.source.fx == null) {
          link.source.vx += fx;
          link.source.vy += fy;
        }
        if (link.target.fx == null) {
          link.target.vx -= fx;
          link.target.vy -= fy;
        }
      });
      
      nodes.forEach((node1, i) => {
        nodes.forEach((node2, j) => {
          if (i >= j) return;
          const dx = node2.x - node1.x;
          const dy = node2.y - node1.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const minDist = node1.size + node2.size + 20;
          
          if (dist < minDist) {
            const force = ((minDist - dist) / dist) * 0.5 * alpha.value;
            const fx = dx * force;
            const fy = dy * force;
            
            if (node1.fx == null) {
              node1.vx -= fx;
              node1.vy -= fy;
            }
            if (node2.fx == null) {
              node2.vx += fx;
              node2.vy += fy;
            }
          }
        });
      });
      
      nodes.forEach(node => {
        if (node.fx != null) {
          node.x = node.fx;
          node.y = node.fy;
        } else {
          node.vx *= 0.6;
          node.vy *= 0.6;
          node.x += node.vx;
          node.y += node.vy;
          
          node.x = Math.max(node.size, Math.min(width - node.size, node.x));
          node.y = Math.max(node.size, Math.min(height - node.size, node.y));
        }
      });
      
      links.forEach(link => {
        link.element.setAttribute('x1', link.source.x);
        link.element.setAttribute('y1', link.source.y);
        link.element.setAttribute('x2', link.target.x);
        link.element.setAttribute('y2', link.target.y);
      });
      
      nodes.forEach(node => {
        node.element.setAttribute('transform', `translate(${node.x}, ${node.y})`);
      });
      
      alpha.value -= alphaDecay;
      simulationRef.current = requestAnimationFrame(tick);
    };
    
    simulationRef.current = requestAnimationFrame(tick);
    
    let viewBox = { x: 0, y: 0, w: width, h: height };
    svg.setAttribute('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`);
    
    let isPanning = false;
    let startPoint = { x: 0, y: 0 };
    
    const onSvgMouseDown = (e) => {
      if (e.target === svg) {
        isPanning = true;
        startPoint = { x: e.clientX, y: e.clientY };
      }
    };
    
    const onSvgMouseMove = (e) => {
      if (!isPanning) return;
      const dx = (e.clientX - startPoint.x) * (viewBox.w / width);
      const dy = (e.clientY - startPoint.y) * (viewBox.h / height);
      viewBox.x -= dx;
      viewBox.y -= dy;
      svg.setAttribute('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`);
      startPoint = { x: e.clientX, y: e.clientY };
    };
    
    const onSvgMouseUp = () => { isPanning = false; };
    
    const onWheel = (e) => {
      e.preventDefault();
      const scale = e.deltaY > 0 ? 1.1 : 0.9;
      const rect = svg.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const svgX = viewBox.x + (mouseX / width) * viewBox.w;
      const svgY = viewBox.y + (mouseY / height) * viewBox.h;
      
      viewBox.w *= scale;
      viewBox.h *= scale;
      viewBox.x = svgX - (mouseX / width) * viewBox.w;
      viewBox.y = svgY - (mouseY / height) * viewBox.h;
      
      svg.setAttribute('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`);
    };
    
    svg.addEventListener('mousedown', onSvgMouseDown);
    svg.addEventListener('mousemove', onSvgMouseMove);
    svg.addEventListener('mouseup', onSvgMouseUp);
    svg.addEventListener('mouseleave', onSvgMouseUp);
    svg.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      if (simulationRef.current) {
        cancelAnimationFrame(simulationRef.current);
      }
      nodes.forEach(node => node.cleanup?.());
      svg.removeEventListener('mousedown', onSvgMouseDown);
      svg.removeEventListener('mousemove', onSvgMouseMove);
      svg.removeEventListener('mouseup', onSvgMouseUp);
      svg.removeEventListener('mouseleave', onSvgMouseUp);
      svg.removeEventListener('wheel', onWheel);
    };
  }, [graphData]);

  const handleGoToVerseThematic = (verse) => {
    // Parse the OSIS reference (e.g., "Gen.1.1") or use bookId/chapter/verse directly
    let bookId = null;
    let chapter = null;
    let verseNum = null;
    
    if (verse.bookId && verse.chapter) {
      bookId = verse.bookId;
      chapter = verse.chapter;
      verseNum = verse.verse;
    } else if (verse.osisId || verse.id) {
      const osisRef = verse.osisId || verse.id;
      const parts = osisRef.split('.');
      if (parts.length >= 2) {
        bookId = parts[0];
        chapter = parts[1];
        if (parts.length >= 3) {
          verseNum = parts[2];
        }
      }
    }
    
    // Validate and sanitize before navigating
    if (bookId && chapter) {
      const safeBookId = String(bookId).replace(/[^a-zA-Z0-9]/g, '');
      const safeChapter = parseInt(chapter, 10);
      const safeVerse = verseNum ? parseInt(verseNum, 10) : null;
      
      if (safeBookId && !isNaN(safeChapter) && safeChapter > 0) {
        if (safeVerse && !isNaN(safeVerse) && safeVerse > 0) {
          navigate(`/reader/${safeBookId}/${safeChapter}/${safeVerse}`);
        } else {
          navigate(`/reader/${safeBookId}/${safeChapter}`);
        }
        return;
      }
    }
    
    // Fallback to reader page root
    navigate('/reader');
  };

  // Save search to history
  const saveToHistory = useCallback((searchQuery) => {
    const newHistory = [
      searchQuery,
      ...searchHistory.filter(h => h !== searchQuery)
    ].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  }, [searchHistory]);

  // Perform search
  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const searchResults = await searchVerses(selectedTranslation, query, { limit: 50 });
      setResults(searchResults || []);
      saveToHistory(query);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, selectedTranslation, saveToHistory]);

  // Navigate to a verse
  const handleGoToVerse = (result) => {
    // Parse the OSIS reference like "Gen.1.1" or use bookId/chapter/verse directly
    let bookId = null;
    let chapter = null;
    let verseNum = null;
    
    if (result.bookId && result.chapter) {
      bookId = result.bookId;
      chapter = result.chapter;
      verseNum = result.verse;
    } else if (result.id) {
      // Parse OSIS reference (e.g., "Gen.1.1")
      const parts = result.id.split('.');
      if (parts.length >= 2) {
        bookId = parts[0];
        chapter = parts[1];
        if (parts.length >= 3) {
          verseNum = parts[2];
        }
      }
    }
    
    // Validate and sanitize before navigating
    if (bookId && chapter) {
      const safeBookId = String(bookId).replace(/[^a-zA-Z0-9]/g, '');
      const safeChapter = parseInt(chapter, 10);
      const safeVerse = verseNum ? parseInt(verseNum, 10) : null;
      
      if (safeBookId && !isNaN(safeChapter) && safeChapter > 0) {
        if (safeVerse && !isNaN(safeVerse) && safeVerse > 0) {
          navigate(`/reader/${safeBookId}/${safeChapter}/${safeVerse}`);
        } else {
          navigate(`/reader/${safeBookId}/${safeChapter}`);
        }
        return;
      }
    }
    
    // Fallback to reader page root
    navigate('/reader');
  };

  // Handle history item click
  const handleHistoryClick = (historyQuery) => {
    setQuery(historyQuery);
    // Auto-search
    setIsLoading(true);
    searchVerses(selectedTranslation, historyQuery, { limit: 50 })
      .then(searchResults => {
        setResults(searchResults || []);
      })
      .catch(err => {
        console.error('Search failed:', err);
        setError('Search failed. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Clear history
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  // Strip HTML from API response (if needed)
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="search-page page">
      <header className="page-header">
        <h1 className="page-title">Search Scripture</h1>
      </header>

      {/* Search Type Tabs */}
      <div className="search-tabs">
        <button 
          className={`tab-button ${activeTab === 'organic' ? 'active' : ''}`}
          onClick={() => setActiveTab('organic')}
        >
          <Icon name="search" size={16} />
          Organic Search
        </button>
        <button 
          className={`tab-button ${activeTab === 'thematic' ? 'active' : ''}`}
          onClick={() => setActiveTab('thematic')}
        >
          <Icon name="themes" size={16} />
          Thematic Search
        </button>
      </div>

      {activeTab === 'organic' && (
        <>
          <div className="search-form-container">
            <form className="search-form" onSubmit={handleSearch}>
              <div className="search-input-wrapper">
                <Icon name="search" size={20} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the Bible..."
                  autoFocus
                />
                {query && (
                  <button 
                    type="button"
                    className="clear-btn"
                    onClick={() => setQuery('')}
                  >
                    <Icon name="x" size={16} />
                  </button>
                )}
              </div>

              <div className="search-options">
                <select
                  className="translation-select"
                  value={selectedTranslation}
                  onChange={(e) => setSelectedTranslation(e.target.value)}
                >
                  {availableBibles.map((bible) => (
                    <option key={bible.id} value={bible.id}>
                      {bible.abbreviation} - {bible.name}
                    </option>
                  ))}
                </select>

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isLoading || !query.trim()}
                >
                  {isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>
          </div>

          <div className="search-content">
            {error && (
              <div className="error-message">
                <Icon name="x" size={18} />
                {error}
              </div>
            )}

            {!query && searchHistory.length > 0 && (
              <div className="search-history">
                <div className="history-header">
                  <h3>Recent Searches</h3>
                  <button className="btn btn-ghost btn-sm" onClick={clearHistory}>
                    Clear
                  </button>
                </div>
                <div className="history-list">
                  {searchHistory.map((historyItem, index) => (
                    <button
                      key={index}
                      className="history-item"
                      onClick={() => handleHistoryClick(historyItem)}
                    >
                      <Icon name="search" size={16} />
                      {historyItem}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading && (
              <div className="loading-state">
                <div className="loading-spinner" />
                <p>Searching Scripture...</p>
              </div>
            )}

            {!isLoading && results.length > 0 && (
              <div className="results-container">
                <div className="results-header">
                  <span className="results-count">{results.length} results found</span>
                </div>
                <div className="results-list">
                  {results.map((result, index) => (
                    <div 
                      key={result.id || index}
                      className="result-item"
                      onClick={() => handleGoToVerse(result)}
                    >
                      <div className="result-reference">{result.reference}</div>
                      <p className="result-text">{stripHtml(result.text)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isLoading && query && results.length === 0 && !error && (
              <div className="no-results">
                <Icon name="search" size={48} />
                <h3>No results found</h3>
                <p>Try different keywords or check your spelling</p>
              </div>
            )}

            {!query && !isLoading && searchHistory.length === 0 && (
              <div className="search-tips">
                <Icon name="themes" size={48} />
                <h3>Search Tips</h3>
                <ul>
                  <li>Search for keywords like "love", "faith", "hope"</li>
                  <li>Search for phrases like "the Lord is my shepherd"</li>
                  <li>Search for references like "John 3:16" or "Psalm 23"</li>
                  <li>Use quotes for exact phrases</li>
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'thematic' && (
        <>
          <div className="hero-explorer">
            <div className="hero-header">
              <h1 className="hero-title">Explore Scripture</h1>
              <p className="hero-subtitle">
                Search for a theme to discover connected verses across the Bible
              </p>
            </div>

            {/* Search Bar */}
            <div className="explorer-search">
              <div className="explorer-search-row">
                <div className="search-input-wrapper">
                  <Icon name="search" size={20} className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    value={thematicQuery}
                    onChange={(e) => setThematicQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleThematicSearch()}
                    placeholder="Enter a theme..."
                  />
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleThematicSearch()}
                  disabled={isThematicSearching || !thematicQuery.trim()}
                >
                  {isThematicSearching ? 'Exploring...' : 'Explore'}
                </button>
              </div>
              <div className="suggested-themes">
                <span className="themes-label">Try:</span>
                {['love', 'faith', 'hope', 'grace', 'mercy', 'peace', 'salvation', 'wisdom'].map(theme => (
                  <button 
                    key={theme}
                    className="theme-chip"
                    onClick={() => handleThematicSearch(theme)}
                  >
                    {theme}
                  </button>
                ))}
              </div>
            </div>

            {/* Graph Visualization */}
            <div className="graph-explorer" ref={containerRef}>
              {graphData.nodes.length === 0 ? (
                <div className="graph-placeholder">
                  <Icon name="themes" size={48} />
                  <p>Search for a theme to see verse connections visualized</p>
                </div>
              ) : (
                <svg ref={svgRef} className="graph-svg" width="100%" height="100%" />
              )}
              
              {hoveredNode && hoveredNode.type === 'verse' && (
                <div className="graph-tooltip">
                  <strong>{hoveredNode.label}</strong>
                  <p>{hoveredNode.text?.substring(0, 120)}...</p>
                </div>
              )}

              {/* Verse Detail Popup */}
              {selectedVerse && (
                <div className="verse-popup">
                  <div className="verse-popup-header">
                    <h3>{selectedVerse.type === 'cluster' ? selectedVerse.label : selectedVerse.label}</h3>
                    <button className="btn btn-ghost btn-icon" onClick={() => setSelectedVerse(null)}>
                      <Icon name="x" size={18} />
                    </button>
                  </div>
                  <div className="verse-popup-content">
                    {selectedVerse.type === 'cluster' ? (
                      <div className="cluster-list">
                        {selectedVerse.verses.slice(0, 5).map((verse, i) => (
                          <div key={i} className="cluster-item" onClick={() => handleGoToVerseThematic(verse)}>
                            <span className="ref">{verse.reference}</span>
                            <p>{verse.text?.substring(0, 80)}...</p>
                          </div>
                        ))}
                        {selectedVerse.verses.length > 5 && (
                          <p className="more-verses">+{selectedVerse.verses.length - 5} more verses</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="verse-full-text">{selectedVerse.text}</p>
                        <button className="btn btn-primary btn-sm" onClick={() => handleGoToVerseThematic(selectedVerse)}>
                          <Icon name="book" size={14} />
                          Open in Reader
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {graphData.nodes.length > 0 && (
              <div className="graph-stats">
                <span>{graphData.nodes.filter(n => n.type === 'verse').length} verses</span>
                <span className="dot">•</span>
                <span>{graphData.nodes.filter(n => n.type === 'cluster').length} clusters</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SearchPage;
